import {
  CharacterCount,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
  useErrorToast,
} from '@h2hspark/ui';
import { Check, Lock } from 'lucide-react';
import * as React from 'react';

import { useSaveConnectionNote } from './api';
import {
  NOTE_DIALOG_PROMPT,
  NOTE_MAX_LENGTH,
  NOTE_PLACEHOLDER,
  NOTE_PRIVACY_QUALIFIER,
  NOTE_SAVE_FAILED,
  NOTE_SAVE_LABEL,
  NOTE_SAVING_LABEL,
  noteDialogTitle,
} from './constants';

/**
 * The note composer opened from a connection card (`Web App.dc.html` §2.16, *Note composer*).
 *
 * ## Why the card opens an overlay rather than linking to the detail
 *
 * The prototype's card has a note button and the button opens this: *"Note about {name}"*, the
 * prompt *"What do you want to remember?"*, the field, the privacy line, and Save. A link to the
 * detail would work and is the wrong shape — a member editing five notes after an event would
 * make five round trips through a screen they did not want, and the grid they were reading would
 * lose its place each time.
 *
 * ## It writes the same field, through the same mutation
 *
 * `useSaveConnectionNote`, which is `PrivateNotePanel`'s. One write path for one field: the two
 * surfaces cannot disagree about trimming, about the length limit, or about what a cleared note
 * means. The mutation invalidates the connections list, so the card behind this overlay redraws
 * with what was saved.
 *
 * **`MEM-033` AC-4 holds here exactly as it does on the detail**, and the qualifier says so
 * beside the field. The note goes to the member's own row; there is no parameter on the mutation
 * that could send it anywhere else.
 *
 * ## What the prototype has that this does not
 *
 * **A Delete button** beside Save, shown once a note exists. Clearing the field and saving is the
 * same operation — the API stores `null` for an empty note, so *"I cleared my note"* and *"I never
 * wrote one"* are one state — and a second control for it is a second thing to keep true. Noted
 * rather than skipped silently.
 *
 * On a phone this is `Dialog`'s full-screen presentation; on desktop it is the prototype's centred
 * 440px card (`MEM-056` AC-3's overlay table). The primitive carries the focus trap and the escape
 * handling (NFR accessibility#wcag-aa).
 */
export function ConnectionNoteDialog({
  open,
  onOpenChange,
  connectionRef,
  name,
  note,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly connectionRef: string;
  readonly name: string;
  readonly note: string | null;
}): React.ReactElement {
  const save = useSaveConnectionNote(connectionRef);
  const [draft, setDraft] = React.useState(note ?? '');
  const showError = useErrorToast();

  // The overlay is mounted by the card and stays mounted, so the draft is re-seeded each time it
  // opens. Without this, editing one note and opening another would show the first one's text.
  React.useEffect(() => {
    if (open) setDraft(note ?? '');
  }, [open, note]);

  React.useEffect(() => {
    if (save.error !== null) showError(save.error, NOTE_SAVE_FAILED);
  }, [save.error, showError]);

  const tooLong = draft.length > NOTE_MAX_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{noteDialogTitle(name)}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {NOTE_PRIVACY_QUALIFIER}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label
            htmlFor="connection-card-note"
            className="block font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle"
          >
            {NOTE_DIALOG_PROMPT}
          </label>
          <Textarea
            id="connection-card-note"
            value={draft}
            placeholder={NOTE_PLACEHOLDER}
            className="min-h-[120px] resize-none"
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <CharacterCount used={draft.length} max={NOTE_MAX_LENGTH} />
        </div>

        <button
          type="button"
          disabled={tooLong || save.isPending}
          onClick={() => {
            save.mutate(
              { note: draft },
              {
                onSuccess: () => {
                  onOpenChange(false);
                },
              },
            );
          }}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-pill bg-brand px-5 py-3 font-brand text-sm font-bold text-white disabled:opacity-50"
        >
          <Check className="h-4 w-4" aria-hidden />
          {save.isPending ? NOTE_SAVING_LABEL : NOTE_SAVE_LABEL}
        </button>
      </DialogContent>
    </Dialog>
  );
}
